import React from "react";
import { Badge } from "@/components/ui/badge";

const STATUS_MAP: Record<string, { label: string; variant?: "default" | "secondary" | "destructive" | "outline"; className?: string }> = {
  // Billing / Invoice statuses
  pending: { label: "Planificado", variant: "outline", className: "border-slate-300 text-slate-700 bg-slate-50" },
  ready_to_invoice: { label: "Listo para facturar", variant: "secondary", className: "bg-blue-50 text-blue-700 border-blue-200" },
  accounting_notified: { label: "Avisado a contabilidad", variant: "secondary", className: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  invoiced: { label: "Factura emitida", variant: "default", className: "bg-emerald-600 hover:bg-emerald-700 text-white" },
  sent_to_client: { label: "Enviado al cliente", variant: "secondary", className: "bg-cyan-50 text-cyan-800 border-cyan-300" },
  pending_collection: { label: "Pendiente de cobro", variant: "secondary", className: "bg-amber-50 text-amber-800 border-amber-300" },
  collected: { label: "Cobrado", variant: "default", className: "bg-green-700 text-white" },
  cancelled: { label: "Cancelado", variant: "destructive", className: "bg-rose-100 text-rose-800 border-rose-200" },
  overdue: { label: "Vencido", variant: "destructive", className: "bg-red-600 text-white" },

  // Budget & document statuses
  confirmed: { label: "Confirmado", variant: "default", className: "bg-emerald-600 text-white" },
  review_required: { label: "Por revisar", variant: "secondary", className: "bg-amber-100 text-amber-900 border-amber-300" },
  failed: { label: "Bloqueado", variant: "destructive", className: "bg-rose-600 text-white" },
  superseded: { label: "Histórico", variant: "outline", className: "text-muted-foreground border-slate-300" },
  historical: { label: "Histórico", variant: "outline", className: "text-muted-foreground border-slate-300" },
  draft: { label: "Borrador", variant: "secondary", className: "bg-slate-100 text-slate-800" },
  approved: { label: "Aprobado", variant: "default", className: "bg-green-600 text-white" },

  // Supplier invoice statuses
  received: { label: "Recibida", variant: "secondary", className: "bg-blue-50 text-blue-800 border-blue-200" },
  paid: { label: "Pagada", variant: "default", className: "bg-emerald-600 text-white" },
  partially_paid: { label: "Pago parcial", variant: "secondary", className: "bg-teal-50 text-teal-800 border-teal-200" },

  // Integrity / severity statuses
  critical: { label: "Crítico", variant: "destructive", className: "bg-red-600 text-white" },
  warning: { label: "Advertencia", variant: "secondary", className: "bg-amber-500 text-white" },
  ok: { label: "Correcto", variant: "secondary", className: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  info: { label: "Información", variant: "outline", className: "border-blue-300 text-blue-700 bg-blue-50" },
};

export interface FinanceStatusBadgeProps {
  status: string;
  labelOverride?: string;
  className?: string;
}

export function humanizeFinanceStatus(status: string): string {
  if (!status) return "—";
  const mapped = STATUS_MAP[status.toLowerCase()];
  if (mapped) return mapped.label;

  // Fallback string transformation if no direct match: replace underscores with spaces & capitalize
  return status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function FinanceStatusBadge({ status, labelOverride, className = "" }: FinanceStatusBadgeProps) {
  const normalizedKey = (status || "").toLowerCase();
  const config = STATUS_MAP[normalizedKey];
  const displayLabel = labelOverride || (config ? config.label : humanizeFinanceStatus(status));

  const variant = config?.variant || "outline";
  const customClass = config?.className || "border-border text-foreground";

  return (
    <Badge variant={variant} className={`text-[11px] font-medium px-2 py-0.5 whitespace-nowrap ${customClass} ${className}`}>
      {displayLabel}
    </Badge>
  );
}
